import { useEffect, useRef, useState } from 'react'
import { Check, FileSearch, LoaderCircle, Table2 } from 'lucide-react'

const stages = [
  { label: 'Upload received', detail: 'The protocol file is ready to analyze.' },
  { label: 'Document structure', detail: 'Reading pages and identifying the table of contents.' },
  { label: 'Section selection', detail: 'Resolving study-build sections and page ranges.' },
  { label: 'Table preservation', detail: 'Keeping schedule tables structured for downstream parsing.' },
]

function formatElapsed(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`
}

/**
 * @param startedAt ISO timestamp of when preparation began. Elapsed time and
 * the active stage are derived from it (not from a local counter) so the
 * display survives unmount/remount — switching tabs away from Documents and
 * back no longer restarts the timer. When omitted, it falls back to the
 * moment of mount.
 */
export default function ProtocolPreparationLoader({ startedAt }: { startedAt?: string | null }) {
  // Anchor the elapsed calculation to a stable start time. A persisted
  // startedAt keeps the clock continuous across remounts; without one we
  // anchor to first mount (held in a ref so re-renders don't move it).
  const mountStartRef = useRef<number>(Date.now())
  const startMs = startedAt ? new Date(startedAt).getTime() : mountStartRef.current

  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])

  const elapsed = Math.max(0, Math.floor((now - startMs) / 1000))
  // Advance one stage every 2.8s, starting at stage 1, holding on the final
  // phase until the request resolves. Derived from elapsed so it, too, is
  // continuous across remounts rather than restarting from the beginning.
  const activeStage = Math.min(1 + Math.floor(elapsed / 2.8), stages.length - 1)

  return (
    <div
      className="border border-primary-200 dark:border-primary-900/70 bg-white dark:bg-gray-900 rounded-xl overflow-hidden"
      role="status"
      aria-live="polite"
      aria-label="Preparing uploaded protocol"
    >
      <div className="relative h-1 bg-primary-100 dark:bg-primary-950 overflow-hidden">
        <div className="absolute inset-y-0 w-1/3 bg-primary-500 rounded-full preparation-progress-slide" />
      </div>
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-4">
          <div className="relative shrink-0 flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 dark:bg-primary-950 text-primary-600 dark:text-primary-300">
            <FileSearch size={21} />
            <LoaderCircle size={46} className="absolute animate-spin text-primary-400/40" strokeWidth={1.25} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">Preparing your protocol</h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">The server is building a structured, auditable extraction preview.</p>
              </div>
              <div className="text-right text-xs text-gray-500 dark:text-gray-400 tabular-nums">
                <div>Elapsed</div>
                <div className="mt-0.5 text-sm font-semibold text-gray-700 dark:text-gray-200">{formatElapsed(elapsed)}</div>
              </div>
            </div>

            <div className="mt-5 rounded-lg bg-gray-50 dark:bg-gray-800/80 px-3 py-2.5 flex items-center gap-2.5">
              <LoaderCircle size={16} className="shrink-0 animate-spin text-primary-600 dark:text-primary-400" />
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Current activity</div>
                <div className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">{stages[activeStage].detail}</div>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {stages.map((stage, index) => {
                const complete = index < activeStage
                const active = index === activeStage
                return (
                  <div key={stage.label} className={`flex items-start gap-2.5 rounded-lg px-2.5 py-2 ${active ? 'bg-primary-50 dark:bg-primary-950/60' : ''}`}>
                    <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${complete ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : active ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300' : 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500'}`}>
                      {complete ? <Check size={13} strokeWidth={2.5} /> : active ? <LoaderCircle size={13} className="animate-spin" /> : <Table2 size={12} />}
                    </span>
                    <div className="min-w-0">
                      <div className={`text-sm ${active ? 'font-semibold text-primary-800 dark:text-primary-200' : complete ? 'font-medium text-gray-700 dark:text-gray-200' : 'text-gray-500 dark:text-gray-400'}`}>{stage.label}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-500">{stage.detail}</div>
                    </div>
                  </div>
                )
              })}
            </div>

            <p className="mt-5 text-xs text-gray-500 dark:text-gray-400">Long protocols may take longer while their tables are preserved. You can stay on this page; the result will appear here when ready.</p>
          </div>
        </div>
      </div>
    </div>
  )
}
